import {
  BarChart2,
  Blocks,
  Bot,
  BookMarked,
  Boxes,
  Database,
  Table2,
  BookOpen,
  FlaskConical,
  HardDrive,
  IdCard,
  Inbox,
  LayoutDashboard,
  LayoutGrid,
  ListChecks,
  MessageSquare,
  Monitor,
  Network,
  Package,
  Phone,
  Shield,
  ShieldCheck,
  ScrollText,
  SlidersHorizontal,
  Sparkles,
  UserPlus,
  Users,
  Users2,
  Workflow,
} from 'lucide-react';

/**
 * Two-level navigation config for the shell.
 *
 * Top level  → Topbar Applications dropdown (left zone)
 * items      → Shell SideNav contents when that section is active
 *
 * When items is empty the SideNav shows only the expand/collapse toggle.
 */
/**
 * Does this section own `pathname`?
 *
 * Matches on whole path segments, **not** a bare `startsWith`: `/dashboards`
 * starts with `/dashboard`, so a prefix test hands every Dashboards URL to the
 * Dashboard section and its sidebar never renders. A section may also claim
 * sibling routes through `matchPaths` — `/widgets` belongs to Dashboards.
 */
export const sectionMatchesPath = (section, pathname = '') =>
  [section.path, ...(section.matchPaths ?? [])].some(
    (candidate) => pathname === candidate || pathname.startsWith(`${candidate}/`),
  );

/** The section that owns `pathname`, or `undefined`. */
export const findActiveSection = (pathname = '') =>
  NAV_CONFIG.find((section) => sectionMatchesPath(section, pathname));

export const NAV_CONFIG = [
  {
    id: 'dashboard',
    path: '/dashboard',
    title: 'Dashboard',
    description: 'Overview and metrics',
    icon: LayoutDashboard,
    items: [],
  },
  {
    id: 'governance',
    path: '/governance',
    title: 'Governance',
    description: 'Policies & knowledge',
    icon: Shield,
    items: [
      { id: '/governance/library',       icon: BookOpen,     labelKey: 'nav.sidebar.library' },
      { id: '/governance/source-drives', icon: HardDrive,    labelKey: 'nav.sidebar.sourceDrives' },
      { id: '/governance/truth-plane',   icon: ShieldCheck,  labelKey: 'nav.sidebar.truthPlane' },
      { id: '/governance/sandbox-plane', icon: FlaskConical, labelKey: 'nav.sidebar.sandboxPlane' },
    ],
  },
  {
    id: 'data-studio',
    path: '/data-studio',
    title: 'Data Studio',
    description: 'Models, entities, tables & reference data',
    icon: Database,
    items: [
      { id: '/data-studio/models',         icon: LayoutGrid, labelKey: 'nav.sidebar.models' },
      { id: '/data-studio/entities',       icon: Boxes,      labelKey: 'nav.sidebar.entities' },
      { id: '/data-studio/tables',         icon: Table2,     labelKey: 'nav.sidebar.tables' },
      { id: '/data-studio/reference-data', icon: BookMarked, labelKey: 'nav.sidebar.referenceData' },
    ],
  },
  {
    id: 'agentic-studio',
    path: '/agentic-studio',
    title: 'Agentic Studio',
    description: 'Agents & workflows',
    icon: Sparkles,
    items: [
      { id: '/agentic-studio/agents',          icon: Bot,           labelKey: 'nav.sidebar.agents' },
      { id: '/agentic-studio/workflows',       icon: Workflow,      labelKey: 'nav.sidebar.workflows' },
      { id: '/agentic-studio/networks',        icon: Network,       labelKey: 'nav.sidebar.networks' },
      { id: '/agentic-studio/voice',           icon: Phone,         labelKey: 'nav.sidebar.voice' },
      { id: '/agentic-studio/chat-widgets',    icon: MessageSquare, labelKey: 'nav.sidebar.chatWidgets' },
      { id: '/agentic-studio/knowledge-packs', icon: Package,       labelKey: 'nav.sidebar.knowledgePacks' },
      { id: '/agentic-studio/htl-packs',       icon: ShieldCheck,   labelKey: 'nav.sidebar.htlPacks' },
      // HTL Settings mirrors the standalone Agentic Studio submenu (Conditions +
      // Teams & Queues + Destinations). The parent id `/agentic-studio/htl-settings`
      // matches every sub-route for active highlighting and, when clicked, the
      // domain's index route redirects to `conditions` (the default section).
      {
        id: '/agentic-studio/htl-settings',
        icon: SlidersHorizontal,
        labelKey: 'nav.sidebar.htlSettings',
        items: [
          { id: '/agentic-studio/htl-settings/conditions',   labelKey: 'nav.sidebar.htlConditions' },
          { id: '/agentic-studio/htl-settings/teams-queues', labelKey: 'nav.sidebar.htlTeamsQueues' },
          { id: '/agentic-studio/htl-settings/destinations', labelKey: 'nav.sidebar.htlDestinations' },
        ],
      },
    ],
  },
  {
    id: 'users',
    path: '/users',
    title: 'User management',
    description: 'Users, roles, and access',
    icon: Users,
    items: [
      { id: '/users',             icon: LayoutGrid, labelKey: 'nav.sidebar.usersOverview' },
      { id: '/users/list',        icon: Users2,     labelKey: 'nav.sidebar.usersList' },
      { id: '/users/roles',       icon: Shield,     labelKey: 'nav.sidebar.roles' },
      { id: '/users/invitations', icon: UserPlus,   labelKey: 'nav.sidebar.invitations' },
      { id: '/users/sessions',    icon: Monitor,    labelKey: 'nav.sidebar.sessions' },
    ],
  },
  {
    id: 'work-queues',
    path: '/work-queues',
    title: 'Work Queues',
    description: 'Triage events that need human attention',
    icon: Inbox,
    // Sidebar sub-items (My Work / My Team / Activity Log) added by CT-4856+.
    items: [],
  },
  {
    // Widgets are the tiles dashboards are built from, so the two share one
    // section and one sidebar rather than appearing as two applications.
    // `/widgets` is a sibling route, not a child of `/dashboards`, which is what
    // `matchPaths` is for.
    id: 'dashboards',
    path: '/dashboards',
    matchPaths: ['/widgets'],
    title: 'Dashboards',
    description: 'Analytics and reports',
    icon: BarChart2,
    items: [
      { id: '/dashboards', icon: BarChart2, labelKey: 'nav.sidebar.dashboards' },
      { id: '/widgets',    icon: Boxes,     labelKey: 'nav.sidebar.widgets' },
      // Where the dashboards built here are consumed. Deliberately NOT added to
      // this section's `matchPaths`: that decides which section OWNS a path, so
      // claiming it would hand every Unified Profiles URL to Dashboards and the
      // Unified Profiles sidebar would never render.
      { id: '/unified-profiles', icon: IdCard, labelKey: 'nav.sidebar.unifiedProfiles' },
    ],
  },
  {
    // Entity profiles: pick a TYPE, pick a RECORD, read its profile. Its own
    // section rather than an item under Dashboards, because the tenant's
    // entities are the subject here and dashboards are just what renders inside
    // one. `items: []` shows only the collapse toggle, as Workspaces does.
    id: 'unified-profiles',
    path: '/unified-profiles',
    title: 'Records',
    description: 'Entity profiles and their dashboards',
    icon: IdCard,
    items: [],
  },
  {
    id: 'workspaces',
    path: '/workspaces',
    title: 'Workspaces',
    description: 'Manage workspaces',
    icon: LayoutGrid,
    items: [],
  },
  {
    id: 'admin-studio',
    path: '/admin-studio',
    title: 'Admin Studio',
    description: 'Integrations & platform administration',
    icon: SlidersHorizontal,
    items: [
      {
        id: 'admin-studio-integrations',
        icon: Blocks,
        labelKey: 'nav.sidebar.integrations',
        items: [
          { id: '/admin-studio/integrations', labelKey: 'nav.sidebar.catalog' },
          { id: '/admin-studio/requests', labelKey: 'nav.sidebar.requests' },
          { id: '/admin-studio/distribution', labelKey: 'nav.sidebar.distribution' },
          { id: '/admin-studio/workspaces', labelKey: 'nav.sidebar.adminWorkspaces' },
        ],
      },
      {
        id: 'admin-studio-workspace',
        icon: ScrollText,
        labelKey: 'nav.sidebar.workspaceGroup',
        items: [
          { id: '/admin-studio/audit', labelKey: 'nav.sidebar.audit' },
        ],
      },
    ],
  },
  {
    id: 'tasks',
    path: '/tasks',
    title: 'My Tasks',
    description: 'Decisions waiting on you',
    icon: ListChecks,
    items: [],
  },
];
